import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatChipListboxChange } from '@angular/material/chips';
import { LabelService } from '../../services/label.service';
import {
  TableInvoiceFields,
  OpzioniTipoFattura,
  StatoFatturaSTS,
  StatoFatturaSDI,
} from '../../models/invoices';
import { Sort } from '@angular/material/sort';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FormGroup, FormControl } from '@angular/forms';

@Component({
  selector: 'app-table-invoices',
  standalone: false,
  templateUrl: './table-invoices.component.html',
  styleUrl: './table-invoices.component.scss',
})
export class TableInvoicesComponent implements OnChanges {
  @Input() invoices: TableInvoiceFields[] = [];
  @Input() isLoadingInvoices: boolean = true;
  @Input() areSDIBillingDataCorrect: boolean = false;
  @Input() areSTSBillingDataCorrect: boolean = true;
  @Output() selectedInvoice = new EventEmitter<TableInvoiceFields>();
  @Output() sentInvoice = new EventEmitter<TableInvoiceFields>();
  @Output() deletedInvoice = new EventEmitter<TableInvoiceFields>();
  @Output() duplicatedInvoice = new EventEmitter<TableInvoiceFields>();
  @Output() showedNotifications = new EventEmitter<TableInvoiceFields>();
  @Output() modifyPaymentDate = new EventEmitter<TableInvoiceFields>();
  @Output() downloadedInvoiceSTSPDF = new EventEmitter<TableInvoiceFields>();
  @Output() downloadedInvoiceSDIPDF = new EventEmitter<TableInvoiceFields>();
  @Output() shownReceipt = new EventEmitter<TableInvoiceFields>();
  @Output() sendEmail = new EventEmitter<TableInvoiceFields>();
  @Output() promotedStsDraft = new EventEmitter<TableInvoiceFields>();
  @Output() viewStsSummary = new EventEmitter<TableInvoiceFields>();
  @Output() issueStsCreditNote = new EventEmitter<TableInvoiceFields>();
  @Output() revertedInvoice = new EventEmitter<TableInvoiceFields>();
  @Output() pagataChanged = new EventEmitter<{
    invoice: TableInvoiceFields;
    checked: boolean;
  }>();
  @Output() sendSelectedInvoices = new EventEmitter<{
    type: 'private' | 'sdi';
    invoices: TableInvoiceFields[];
  }>();
  @Output() deleteSelectedInvoices = new EventEmitter<{
    type: 'private' | 'sdi';
    invoices: TableInvoiceFields[];
  }>();
  filteredInvoices: TableInvoiceFields[] = [];
  filters: { [key: string]: string } = {};

  selectedChips: string[] = ['all'];

  currentYear = new Date().getFullYear();

  sortDirections: { [key: string]: 'asc' | 'desc' | null } = {
    progressivo_numero: null,
    progressivo_anno: null,
    data: null,
  };

  showClientSearch = false;
  clientSearchValue = '';
  displayedColumns: string[] = [
    'select',
    'number',
    'year',
    'status',
    'date',
    'type',
    'client',
    'total',
    'data_pagamento',
    'pagata',
    'actions',
  ];
  selectedInvoices: Set<TableInvoiceFields> = new Set();
  selectedPrivateInvoices: TableInvoiceFields[] = [];
  selectedSDIInvoices: TableInvoiceFields[] = [];

  dateRange = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  showDatePicker = false;

  dateRangePanel = {
    isOpen: false,
    startDate: null as Date | null,
    endDate: null as Date | null,
  };

  constructor(
    public labelService: LabelService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['invoices']) {
      this.filteredInvoices = this.invoices;
      this.selectedChips = ['all'];
      this.filterInvoicesByChips();
    }
  }

  sort(column: 'progressivo_numero' | 'progressivo_anno' | 'data'): void {
    Object.keys(this.sortDirections).forEach((key) => {
      if (key !== column) {
        this.sortDirections[key] = null;
      }
    });

    this.sortDirections[column] =
      this.sortDirections[column] === 'asc' ? 'desc' : 'asc';

    this.filteredInvoices.sort((a, b) => {
      if (column === 'data') {
        return this.compareDates(
          a[column],
          b[column],
          this.sortDirections[column] === 'asc'
        );
      } else {
        const valueA = a[column];
        const valueB = b[column];

        const numA = parseInt(String(valueA));
        const numB = parseInt(String(valueB));

        if (this.sortDirections[column] === 'asc') {
          return numA - numB;
        } else {
          return numB - numA;
        }
      }
    });
  }

  private compareDates(
    dateA: string,
    dateB: string,
    isAscending: boolean
  ): number {
    // Split the dates into their components
    const [dayA, monthA, yearA] = dateA.split('/').map(Number);
    const [dayB, monthB, yearB] = dateB.split('/').map(Number);

    // Compare year first
    if (yearA !== yearB) {
      return isAscending ? yearA - yearB : yearB - yearA;
    }

    // If years are equal, compare months
    if (monthA !== monthB) {
      return isAscending ? monthA - monthB : monthB - monthA;
    }

    // If months are equal, compare days
    return isAscending ? dayA - dayB : dayB - dayA;
  }

  getSortIcon(
    column: 'progressivo_numero' | 'progressivo_anno' | 'data'
  ): string {
    if (this.sortDirections[column] === null) return 'unfold_more';
    return this.sortDirections[column] === 'asc'
      ? 'arrow_upward'
      : 'arrow_downward';
  }

  applyFilter(column: string, value: string) {
    this.filters[column] = value.toLowerCase();
    this.filterInvoices();
  }

  filterInvoices() {
    this.filteredInvoices = this.invoices.filter((invoice) => {
      return Object.keys(this.filters).every((key) => {
        return String(invoice[key as keyof TableInvoiceFields])
          .toLowerCase()
          .includes(this.filters[key]);
      });
    });
  }

  openInvoiceActions(event: Event): void {
    event.stopPropagation();
  }

  duplicateInvoice(invoice: TableInvoiceFields): void {
    this.duplicatedInvoice.emit(invoice);
  }

  modifyInvoice(invoice: TableInvoiceFields): void {
    this.selectedInvoice.emit(invoice);
  }

  sendInvoice(invoice: TableInvoiceFields): void {
    this.sentInvoice.emit(invoice);
  }

  showNotifications(invoice: TableInvoiceFields): void {
    this.showedNotifications.emit(invoice);
  }

  deleteInvoice(invoice: TableInvoiceFields): void {
    this.deletedInvoice.emit(invoice);
  }

  modifyInvoicePaymentDate(invoice: TableInvoiceFields): void {
    this.modifyPaymentDate.emit(invoice);
  }

  downloadInvoiceSTSPDF(invoice: TableInvoiceFields): void {
    this.downloadedInvoiceSTSPDF.emit(invoice);
  }

  downloadInvoiceSDIPDF(invoice: TableInvoiceFields): void {
    this.downloadedInvoiceSDIPDF.emit(invoice);
  }

  sendEmailToClient(invoice: TableInvoiceFields): void {
    this.sendEmail.emit(invoice);
  }

  onChipSelectionChange(event: MatChipListboxChange): void {
    const value = event.value as string[];
    this.selectedInvoices.clear();
    this.updateSelectedInvoicesArrays();

    // If no chips are selected, select 'all'
    if (value.length === 0) {
      this.selectedChips = ['all'];
      this.filterInvoicesByChips();
      return;
    }

    if (value.length > 0) {
      if (value.includes('all')) {
        this.selectedChips = value.filter((chip) => chip !== 'all');
      } else {
        this.selectedChips = value;
      }
      this.filterInvoicesByChips();
      return;
    }
  }

  showReceipt(invoice: TableInvoiceFields): void {
    this.shownReceipt.emit(invoice);
  }

  revertInvoice(invoice: TableInvoiceFields): void {
    this.revertedInvoice.emit(invoice);
  }

  promoteStsDraftToReady(invoice: TableInvoiceFields): void {
    this.promotedStsDraft.emit(invoice);
  }

  openStsSummary(invoice: TableInvoiceFields): void {
    this.viewStsSummary.emit(invoice);
  }

  openStsCreditNote(invoice: TableInvoiceFields): void {
    this.issueStsCreditNote.emit(invoice);
  }

  private filterInvoicesByChips(): void {
    if (this.selectedChips.includes('all')) {
      this.filteredInvoices = this.invoices;
      return;
    }

    this.filteredInvoices = this.invoices.filter((invoice) => {
      return this.selectedChips.every((chip) => {
        switch (chip) {
          case 'ready':
            return invoice.sendable && invoice.stato !== StatoFatturaSTS.BOZZA;
          case 'private':
            return invoice.tipo === OpzioniTipoFattura.PARCELLA_PRIVATI;
          case 'company':
            return invoice.tipo === OpzioniTipoFattura.PARCELLA_SDI;
          case 'credit_note_sdi':
            return invoice.tipo === OpzioniTipoFattura.NOTA_CREDITO_SDI;
          case 'year':
            return (
              invoice.progressivo_anno.toString() ===
              this.currentYear.toString()
            );
          case 'date_range':
            const start = this.dateRange.get('start')?.value;
            const end = this.dateRange.get('end')?.value;
            if (!start || !end) return true;
            const [day, month, year] = invoice.data.split('/').map(Number);
            const invoiceDate = new Date(year, month - 1, day);
            return invoiceDate >= start && invoiceDate <= end;
          default:
            return true;
        }
      });
    });
  }

  toggleClientSearch(): void {
    this.showClientSearch = !this.showClientSearch;
    if (!this.showClientSearch) {
      this.clientSearchValue = '';
      this.filteredInvoices = this.invoices;
    }
  }

  onClientSearchChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.clientSearchValue = value;

    if (!value.trim()) {
      this.filteredInvoices = this.invoices;
      return;
    }

    this.filteredInvoices = this.invoices.filter((invoice) =>
      invoice.client_name.toLowerCase().includes(value.toLowerCase())
    );
  }

  clearSearch(): void {
    this.clientSearchValue = '';
    this.filteredInvoices = this.invoices;
  }

  onSort(sortState: Sort) {
    if (sortState.direction === '') {
      // If direction is empty, reset to original data
      this.filteredInvoices = [...this.filteredInvoices];
      return;
    }

    this.filteredInvoices = [...this.filteredInvoices].sort((a, b) => {
      const isAsc = sortState.direction === 'asc';
      switch (sortState.active) {
        case 'progressivo_numero':
          return this.compare(
            a.progressivo_numero,
            b.progressivo_numero,
            isAsc
          );
        case 'progressivo_anno':
          return this.compare(a.progressivo_anno, b.progressivo_anno, isAsc);
        case 'data':
          return this.compare(
            this.parseDate(a.data),
            this.parseDate(b.data),
            isAsc
          );
        default:
          return 0;
      }
    });
  }

  private compare(a: any, b: any, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  private parseDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  onPagataChange(invoice: TableInvoiceFields, event: MatCheckboxChange): void {
    if (!this.isDraftOrReadyStatus(invoice)) {
      return;
    }
    this.pagataChanged.emit({ invoice, checked: event.checked });
  }

  isDraftOrReadyStatus(invoice: TableInvoiceFields): boolean {
    return (
      invoice.stato === StatoFatturaSTS.BOZZA ||
      invoice.stato === StatoFatturaSTS.PRONTA
    );
  }

  onRowClick(event: MouseEvent, row: any): void {
    const target = event.target as HTMLElement;
    if (!target.closest('mat-checkbox') && !target.closest('.action-button')) {
      this.onRowSelection(row);
    }
  }
  onRowSelection(invoice: TableInvoiceFields): void {
    if (this.selectedInvoices.has(invoice)) {
      this.selectedInvoices.delete(invoice);
    } else {
      this.selectedInvoices.add(invoice);
    }
    this.updateSelectedInvoicesArrays();
  }

  isSelected(invoice: TableInvoiceFields): boolean {
    return this.selectedInvoices.has(invoice);
  }

  private updateSelectedInvoicesArrays(): void {
    this.selectedPrivateInvoices = Array.from(this.selectedInvoices).filter(
      (inv) => inv.tipo === OpzioniTipoFattura.PARCELLA_PRIVATI
    );

    this.selectedSDIInvoices = Array.from(this.selectedInvoices).filter(
      (inv) =>
        inv.tipo === OpzioniTipoFattura.PARCELLA_SDI ||
        inv.tipo === OpzioniTipoFattura.NOTA_CREDITO_SDI
    );
  }

  canSendInvoices(type: 'private' | 'sdi'): boolean {
    const invoices =
      type === 'private'
        ? this.selectedPrivateInvoices
        : this.selectedSDIInvoices;
    return (
      invoices.length > 0 &&
      invoices.every((inv) => inv.sendable && this.canSendInvoice(inv))
    );
  }

  canDeleteInvoices(type: 'private' | 'sdi'): boolean {
    const invoices =
      type === 'private'
        ? this.selectedPrivateInvoices
        : this.selectedSDIInvoices;
    return invoices.length > 0 && invoices.every((inv) => inv.deletable);
  }

  getNotSendableInvoices(type: 'private' | 'sdi'): string {
    const invoices =
      type === 'private'
        ? this.selectedPrivateInvoices
        : this.selectedSDIInvoices;
    return invoices
      .filter((inv) => !inv.sendable || !this.canSendInvoice(inv))
      .map((inv) => inv.progressivo_numero)
      .join(', ');
  }

  getNotDeletableInvoices(type: 'private' | 'sdi'): string {
    const invoices =
      type === 'private'
        ? this.selectedPrivateInvoices
        : this.selectedSDIInvoices;
    return invoices
      .filter((inv) => !inv.deletable)
      .map((inv) => inv.progressivo_numero)
      .join(', ');
  }

  onSendSelected(type: 'private' | 'sdi'): void {
    const invoices =
      type === 'private'
        ? this.selectedPrivateInvoices
        : this.selectedSDIInvoices;
    switch (type) {
      case 'private':
        this.selectedPrivateInvoices = [];
        break;
      case 'sdi':
        this.selectedSDIInvoices = [];
        break;
    }
    this.sendSelectedInvoices.emit({ type, invoices });
  }

  onDeleteSelected(type: 'private' | 'sdi'): void {
    const invoices =
      type === 'private'
        ? this.selectedPrivateInvoices
        : this.selectedSDIInvoices;
    switch (type) {
      case 'private':
        this.selectedPrivateInvoices = [];
        break;
      case 'sdi':
        this.selectedSDIInvoices = [];
        break;
    }
    this.deleteSelectedInvoices.emit({ type, invoices });
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.selectedInvoices.clear();
    } else {
      this.filteredInvoices.forEach((row) => this.selectedInvoices.add(row));
    }
    this.updateSelectedInvoicesArrays();
  }

  isAllSelected() {
    const numSelected = this.selectedInvoices.size;
    const numRows = this.filteredInvoices.length;
    return numSelected === numRows && numRows > 0;
  }

  isIndeterminate() {
    return !this.isAllSelected() && this.selectedInvoices.size > 0;
  }

  toggleDatePicker(): void {
    this.dateRangePanel.isOpen = !this.dateRangePanel.isOpen;

    if (this.dateRangePanel.isOpen) {
      // When opening, set the panel dates to the current filter dates
      this.dateRange.patchValue({
        start: this.dateRangePanel.startDate,
        end: this.dateRangePanel.endDate,
      });
    }
  }

  confirmDateRange(): void {
    const start = this.dateRange.get('start')?.value;
    const end = this.dateRange.get('end')?.value;

    if (start && end) {
      this.dateRangePanel.startDate = start;
      this.dateRangePanel.endDate = end;
      this.dateRangePanel.isOpen = false;

      if (!this.selectedChips.includes('date_range')) {
        this.selectedChips.push('date_range');
      }
      this.filterInvoicesByChips();
    }
  }

  resetDateRange(): void {
    this.dateRange.reset();
    this.dateRangePanel.startDate = null;
    this.dateRangePanel.endDate = null;
    this.selectedChips = this.selectedChips.filter(
      (chip) => chip !== 'date_range'
    );
    this.dateRangePanel.isOpen = false;
    this.filterInvoicesByChips();
  }

  isInvoiceSTS(invoice: TableInvoiceFields): boolean {
    return invoice.tipo === OpzioniTipoFattura.PARCELLA_PRIVATI;
  }

  private isStsSendableType(invoice: TableInvoiceFields): boolean {
    return (
      invoice.tipo === OpzioniTipoFattura.PARCELLA_PRIVATI ||
      invoice.tipo === OpzioniTipoFattura.NOTA_CREDITO_STS
    );
  }

  /** STS expenses must be paid before they can be sent. */
  canSendInvoice(invoice: TableInvoiceFields): boolean {
    if (this.isStsSendableType(invoice)) {
      return invoice.pagata;
    }
    return true;
  }

  isStsSendActionVisible(invoice: TableInvoiceFields): boolean {
    return invoice.sendable && this.isStsSendableType(invoice);
  }

  isStsDraft(invoice: TableInvoiceFields): boolean {
    return (
      this.isInvoiceSTS(invoice) && invoice.stato === StatoFatturaSTS.BOZZA
    );
  }

  isStsReady(invoice: TableInvoiceFields): boolean {
    return (
      this.isInvoiceSTS(invoice) && invoice.stato === StatoFatturaSTS.PRONTA
    );
  }

  isInvoiceSTSSent(invoice: TableInvoiceFields): boolean {
    return (
      invoice.stato === StatoFatturaSTS.ACCOLTA_SENZA_SEGNALAZIONI ||
      invoice.stato === StatoFatturaSTS.ACCOLTA_CON_SEGNALAZIONI
    );
  }

  isStsCreditNoteEligible(invoice: TableInvoiceFields): boolean {
    return this.isInvoiceSTS(invoice) && this.isInvoiceSTSSent(invoice);
  }

  isInvoiceReady(invoice: TableInvoiceFields): boolean {
    return invoice.stato === StatoFatturaSTS.PRONTA;
  }

  isInvoiceReadyOrSent(invoice: TableInvoiceFields): boolean {
    switch (invoice.tipo) {
      case OpzioniTipoFattura.PARCELLA_PRIVATI:
        return invoice.stato !== StatoFatturaSTS.BOZZA;
      case OpzioniTipoFattura.PARCELLA_SDI:
        return invoice.stato !== StatoFatturaSDI.BOZZA;
      default:
        return false;
    }
  }
}
